import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Bookmark } from "@mui/icons-material";
import { fetchMyVenueBookings } from "./api";
import type { VenueBooking } from "./types";
import {
  formatSlotRange,
  formatVenueBookingPrice,
  isValidVenueBooking,
  isVenueBookingInPast,
} from "./venueHireUtils";

interface MyVenueBookingsProps {
  clubId: number;
  refreshKey?: number;
  reschedulingBookingId?: number | null;
  onCancel: (booking: VenueBooking) => void;
  onRearrange: (booking: VenueBooking) => void;
}

const MyVenueBookings: React.FC<MyVenueBookingsProps> = ({
  clubId,
  refreshKey = 0,
  reschedulingBookingId = null,
  onCancel,
  onRearrange,
}) => {
  const [bookings, setBookings] = useState<VenueBooking[]>([]);
  const [loading, setLoading] = useState(true);

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchMyVenueBookings(clubId);
      setBookings(data);
    } catch (err) {
      console.error("Error loading my venue bookings:", err);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings, refreshKey]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2, mb: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (bookings.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle1"
        sx={{
          display: "flex",
          alignItems: "center",
          fontWeight: 700,
          mb: 1.5,
        }}
      >
        <Bookmark sx={{ mr: 1, fontSize: 20, color: "#667eea" }} />
        Your bookings
      </Typography>
      <Stack spacing={1.5}>
        {bookings.map((booking) => {
          if (!isValidVenueBooking(booking)) {
            return null;
          }
          const isRescheduling = reschedulingBookingId === booking.id;
          const canRearrange = !isVenueBookingInPast(booking);
          return (
            <Paper
              key={booking.id}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: isRescheduling ? "primary.light" : "divider",
                bgcolor: isRescheduling ? "action.selected" : "background.paper",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatSlotRange(
                      booking.date,
                      booking.start_time,
                      booking.end_time,
                    )}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 1 }}
                  >
                    <Chip
                      label={formatVenueBookingPrice(booking.price)}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={booking.paid ? "Paid" : "Awaiting payment"}
                      size="small"
                      color={booking.paid ? "success" : "warning"}
                    />
                    {booking.invoice_id && !booking.paid && (
                      <Chip
                        label={`INV-${booking.invoice_id}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1}>
                  {canRearrange && (
                    <Button
                      size="small"
                      variant={isRescheduling ? "contained" : "outlined"}
                      onClick={() => onRearrange(booking)}
                    >
                      {isRescheduling ? "Selecting slot…" : "Rearrange"}
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => onCancel(booking)}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default MyVenueBookings;
